const ROOT_SELECTOR = '[data-br-cookie-consent]';
const OPEN_SELECTOR = '[data-br-cookie-preferences-open]';
const CONSENT_KEYS = ['analytics', 'marketing', 'preferences'];
// Functional first-party preference: saved only after a visitor explicitly chooses
// and Shopify's consent API confirms the decision. It never grants tracking.
const CONSENT_CHOICE_KEY = 'braymont-cookie-consent-choice-v2';
const CONSENT_CHOICE_LIFETIME = 365 * 24 * 60 * 60 * 1000;


let activeRoot = null;
let privacyApi = null;
let initialChoiceRequired = false;
let returnFocus = null;

function answered(value) {
  return value === 'yes' || value === 'no' || typeof value === 'boolean';
}

function allowed(value) {
  return value === 'yes' || value === true;
}

function currentConsent() {
  try {
    return privacyApi?.currentVisitorConsent?.() || {};
  } catch {
    return {};
  }
}

function hasCompleteChoice(consent = currentConsent()) {
  return CONSENT_KEYS.every((key) => answered(consent[key]));
}

function readLocalChoice() {
  let raw = null;
  try { raw = window.localStorage.getItem(CONSENT_CHOICE_KEY); } catch {}
  if (!raw) {
    try { raw = window.sessionStorage.getItem(CONSENT_CHOICE_KEY); } catch {}
  }
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw);
    if (!saved || !Number.isFinite(saved.timestamp) ||
        saved.timestamp > Date.now() ||
        Date.now() - saved.timestamp >= CONSENT_CHOICE_LIFETIME ||
        !saved.choices ||
        !CONSENT_KEYS.every((key) => typeof saved.choices[key] === 'boolean')) {
      return null;
    }
    return saved.choices;
  } catch { return null; }
}

function rememberConfirmedChoice(choices) {
  const record = JSON.stringify({
    timestamp: Date.now(),
    choices: {
      analytics: choices.analytics === true,
      marketing: choices.marketing === true,
      preferences: choices.preferences === true,
    },
  });
  try { window.localStorage.setItem(CONSENT_CHOICE_KEY, record); }
  catch {
    // Session fallback for browsers where persistent storage is blocked.
    try { window.sessionStorage.setItem(CONSENT_CHOICE_KEY, record); } catch {}
  }
}

// Shopify's registered consent is the authority; browser storage is never
// used as proof of permission to run optional tracking.
function needsBanner() {
  if (hasCompleteChoice()) return false;
  const region = String(privacyApi?.getRegion?.() || '').toUpperCase();
  let configuredBanner = false;
  try { configuredBanner = privacyApi?.shouldShowBanner?.() === true; }
  catch {}
  // The Braymont UK market requires prior consent. This fallback also covers
  // custom-banner configurations where Shopify's native banner is switched off.
  return configuredBanner || region.startsWith('GB') || !region;
}

function choiceMatches(choices) {
  const values = currentConsent();
  return CONSENT_KEYS.every((key) => answered(values[key]) &&
    allowed(values[key]) === (choices[key] === true));
}

async function waitForSavedChoice(choices) {
  for (let i = 0; i < 25; i += 1) {
    if (choiceMatches(choices)) return;
    await new Promise((resolve) => window.setTimeout(resolve, 200));
  }
  throw new Error('Shopify did not persist all selected consent categories.');
}

function setBusy(root, busy) {
  root.querySelectorAll('button').forEach((button) => {
    button.disabled = busy;
  });
  root.toggleAttribute('aria-busy', busy);
}

function setStatus(root, message = '', isError = false) {
  const statuses = root.querySelectorAll('[data-br-cookie-status]');
  statuses.forEach((status) => {
    status.textContent = message;
    status.classList.toggle('is-error', Boolean(message && isError));
  });
}

function lockPage(locked) {
  document.documentElement.classList.toggle('br-cookie-preferences-open', locked);
}

function showBanner(root) {
  root.hidden = false;
  const banner = root.querySelector('[data-br-cookie-banner]');
  banner.hidden = false;
  root.querySelector('[data-br-cookie-modal-layer]').hidden = true;
  // A prominent non-modal panel: focus and scrolling stay on the storefront.
  banner.removeAttribute('aria-modal');
  banner.setAttribute('role', 'region');
  lockPage(false);
}

function hideAll(root) {
  root.hidden = true;
  root.querySelector('[data-br-cookie-banner]').hidden = true;
  root.querySelector('[data-br-cookie-modal-layer]').hidden = true;
  lockPage(false);
  returnFocus?.focus?.({ preventScroll: true });
  returnFocus = null;
}

function populatePreferences(root) {
  const consent = currentConsent();
  // A shopper can reopen the preference panel even if Shopify's cookie state
  // hasn't hydrated on this page. Display their previously confirmed choice.
  const saved = hasCompleteChoice(consent) ? null : readLocalChoice();
  for (const key of CONSENT_KEYS) {
    root.querySelector('[data-consent="' + key + '"]').checked =
      saved ? saved[key] : allowed(consent[key]);
  }
}

function focusableElements(container) {
  return [...container.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]')]
    .filter((element) => !element.hidden && element.offsetParent !== null);
}

function focusDialog(root) {
  requestAnimationFrame(() => {
    const dialog = root.querySelector('[data-br-cookie-dialog]');
    const first = focusableElements(dialog)[0] || dialog;
    first.focus({ preventScroll: true });
  });
}

function showPreferences(root, opener = document.activeElement) {
  returnFocus = opener instanceof HTMLElement ? opener : null;
  root.hidden = false;
  root.querySelector('[data-br-cookie-banner]').hidden = true;
  root.querySelector('[data-br-cookie-modal-layer]').hidden = false;
  populatePreferences(root);
  setStatus(root);
  lockPage(true);
  focusDialog(root);
}

function finishConsent(root, choices) {
  // Only write the functional preference after the API has confirmed that
  // the visitor's explicit selection was successfully registered.
  rememberConfirmedChoice(choices);
  initialChoiceRequired = false;
  setBusy(root, false);
  setStatus(root);
  hideAll(root);
  document.dispatchEvent(new CustomEvent('braymont:cookie-consent-updated', {
    detail: currentConsent(),
  }));
}

async function applyConsent(root, choices) {
  // A choice counts as saved only after the Shopify Customer Privacy API confirms.
  if (root.hasAttribute('aria-busy')) return;
  setBusy(root, true);
  setStatus(root, root.dataset.savingMessage || 'Saving your preferences…');

  try {
    // Retry API loading on visitor interaction if initial loading was unavailable.
    privacyApi = privacyApi?.setTrackingConsent ? privacyApi : await loadPrivacyApi();

    await new Promise((resolve, reject) => {
      let finished = false;
      const timeoutId = window.setTimeout(() => {
        if (finished) return;
        finished = true;
        reject(new Error('Shopify consent request timed out.'));
      }, 12000);

      try {
        // Data-sale opt-out is a separate regional flow; do not force it
        // alongside Accept all, Essential only, or custom cookie preferences.
        privacyApi.setTrackingConsent(
          {
            analytics: choices.analytics === true,
            marketing: choices.marketing === true,
            preferences: choices.preferences === true,
          },
          (result) => {
            if (finished) return;
            finished = true;
            window.clearTimeout(timeoutId);

            if (result?.error && (!Array.isArray(result.error) || result.error.length > 0)) {
              const message = Array.isArray(result.error)
                ? result.error.join('; ')
                : String(result.error);
              reject(new Error(message || 'Shopify rejected the consent request.'));
              return;
            }
            resolve();
          },
        );
      } catch (error) {
        if (finished) return;
        finished = true;
        window.clearTimeout(timeoutId);
        reject(error);
      }
    });

    await waitForSavedChoice(choices);
    finishConsent(root, choices);
  } catch (error) {
    // Do not grant optional consent or permanently trap customers on API failures.
    console.warn('[braymont-cookie-consent] Unable to save Shopify consent:', error);
    setBusy(root, false);
    root.classList.add('br-cookie-consent--api-unavailable');
    lockPage(false);
    setStatus(root, root.dataset.errorMessage || 'We could not save your choice. Please try again.', true);
  }
}

function acceptAll(root) {
  applyConsent(root, { analytics: true, marketing: true, preferences: true });
}

function essentialOnly(root) {
  applyConsent(root, { analytics: false, marketing: false, preferences: false });
}

function savePreferences(root) {
  applyConsent(root, {
    analytics: root.querySelector('[data-consent="analytics"]').checked,
    preferences: root.querySelector('[data-consent="preferences"]').checked,
    marketing: root.querySelector('[data-consent="marketing"]').checked,
  });
}

function handleBack(root) {
  if (initialChoiceRequired) {
    showBanner(root);
  } else {
    hideAll(root);
  }
}

function handleKeydown(event, root) {
  if (root.hidden) return;
  // Focus trapping only belongs to the explicit preferences dialog, not the
  // initial banner; keyboard users must be able to navigate the store.
  const layer = root.querySelector('[data-br-cookie-modal-layer]');
  if (layer.hidden) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    handleBack(root);
    return;
  }
  if (event.key !== 'Tab') return;
  const dialog = root.querySelector('[data-br-cookie-dialog]');
  const focusable = focusableElements(dialog);
  if (!focusable.length) {event.preventDefault(); dialog.focus(); return;}
  const first = focusable[0], last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {event.preventDefault();last.focus();}
  else if (!event.shiftKey && document.activeElement === last) {event.preventDefault();first.focus();}
}

function bindRoot(root) {
  if (root.dataset.brCookieReady === 'true') return;
  root.dataset.brCookieReady = 'true';

  root.addEventListener('click', (event) => {
    const button = event.target.closest('[data-br-cookie-action]');
    if (!button) return;

    const action = button.dataset.brCookieAction;
    if (action === 'accept-all') acceptAll(root);
    if (action === 'essential-only') essentialOnly(root);
    if (action === 'manage') showPreferences(root, button);
    if (action === 'save') savePreferences(root);
    if (action === 'back') handleBack(root);
  });

  root.addEventListener('keydown', (event) => handleKeydown(event, root));
}

function bindOpeners(root) {
  document.querySelectorAll(OPEN_SELECTOR).forEach((button) => {
    if (button.dataset.brCookieOpenerReady === 'true') return;
    button.dataset.brCookieOpenerReady = 'true';
    if (root.dataset.preferencesLabel) button.textContent = root.dataset.preferencesLabel;
    button.addEventListener('click', () => showPreferences(root, button));
  });
}

function loadPrivacyApi() {
  return new Promise((resolve, reject) => {
    if (typeof window.Shopify?.customerPrivacy?.setTrackingConsent === 'function') {
      resolve(window.Shopify.customerPrivacy);
      return;
    }

    if (typeof window.Shopify?.loadFeatures !== 'function') {
      reject(new Error('Shopify Customer Privacy API is unavailable.'));
      return;
    }

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.2' }],
      (error) => {
        if (error || !window.Shopify?.customerPrivacy) {
          reject(error || new Error('Shopify Customer Privacy API did not load.'));
          return;
        }
        resolve(window.Shopify.customerPrivacy);
      },
    );
  });
}

async function init() {
  const root = document.querySelector(ROOT_SELECTOR);
  if (!root) return;

  activeRoot = root;
  bindRoot(root);
  bindOpeners(root);

  try {
    privacyApi = await loadPrivacyApi();
    root.classList.remove('br-cookie-consent--api-unavailable');
    initialChoiceRequired = needsBanner();
    if (initialChoiceRequired) showBanner(root);
    else hideAll(root);
  } catch (error) {
    console.warn('[braymont-cookie-consent] Unable to load Customer Privacy API:', error);
    if (readLocalChoice()) {
      initialChoiceRequired = false;
      hideAll(root);
    } else {
      // Non-blocking error state, without granting any optional cookie permissions.
      initialChoiceRequired = true;
      root.classList.add('br-cookie-consent--api-unavailable');
      showBanner(root);
      setStatus(root, 'Privacy preferences could not be loaded. You may continue browsing; optional tracking is not enabled. Please try again.', true);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target?.querySelector?.(ROOT_SELECTOR)) init();
});

document.addEventListener('visitorConsentCollected', () => {
  if (!activeRoot || activeRoot.hasAttribute('aria-busy')) return;
  // Consent might be recorded through another Shopify surface, so do not
  // depend on a second localStorage copy before hiding the notice.
  if (hasCompleteChoice()) {
    initialChoiceRequired = false;
    hideAll(activeRoot);
  }
});

export { activeRoot };
